import { getDenoiserExtension } from '../agoraLoader';
import loggerService from '../logger.service';

import type { DenoiserHook } from './types';

/**
 * v1 denoiser hook (agora-extension-ai-denoiser ^1.1.0).
 *
 * v1 has no destroy() — every createProcessor() call leaks ~19 MB of WASM
 * memory + a fresh AudioWorkletNode (CSD-78018). The only leak-proof rule
 * for v1 is "allocate once, reuse forever":
 *   - createProcessor() returns the cached instance if one exists.
 *   - destroyProcessor() detaches + removes listeners but keeps the instance
 *     alive so the next createProcessor() can reuse it.
 *
 * v1 1.1.0 deprecated the assignment-style callbacks (extension.onloaderror,
 * processor.onoverload) in favour of the EventEmitter API on the processor
 * (processor.on('loaderror' | 'overload', ...)). This hook uses the new API
 * exclusively. AIDenoiserProcessor extends AudioProcessor → EventEmitter, so
 * on/off/removeAllListeners and the audio methods (disable/unpipe) are
 * guaranteed by the v1 contract.
 */

let cachedProcessor: any = null;

function attachLoaderrorHandler(processor: any) {
  processor.removeAllListeners('loaderror');
  processor.on('loaderror', (error: Error) => {
    loggerService.error('[DenoiserV1] loaderror:', error);
  });
}

async function detachFromPipeline(processor: any): Promise<void> {
  try {
    await processor.disable();
  } catch (e) {
    loggerService.warn('[DenoiserV1] processor.disable() during detach:', e);
  }
  try {
    await processor.unpipe();
  } catch (e) {
    loggerService.warn('[DenoiserV1] processor.unpipe() during detach:', e);
  }
}

export default function useDenoiserV1(): DenoiserHook {
  return {
    createProcessor() {
      const denoiser = getDenoiserExtension();
      if (!denoiser) {
        loggerService.error('[DenoiserV1] Extension not available when creating processor');
        return null;
      }

      if (cachedProcessor) {
        attachLoaderrorHandler(cachedProcessor);
        return cachedProcessor;
      }

      const processor = denoiser.createProcessor();
      attachLoaderrorHandler(processor);
      cachedProcessor = processor;
      return processor;
    },

    setupOverloadHandler(processor: any, onOverload: (elapsedMs?: number) => void) {
      processor.removeAllListeners('overload');
      processor.on('overload', (elapsedMs?: number) => {
        loggerService.warn('[DenoiserV1] overload event fired', { elapsedMs });
        onOverload(elapsedMs);
      });
    },

    configureAfterPipe() {
      // No-op: v1 has no setLatency equivalent.
    },

    detachProcessor(processor: any) {
      return detachFromPipeline(processor);
    },

    async destroyProcessor(processor: any) {
      await detachFromPipeline(processor);
      processor.removeAllListeners('loaderror');
      processor.removeAllListeners('overload');
      processor.removeAllListeners('dump');
      processor.removeAllListeners('dumpend');
    },

    // Keep cachedProcessor alive so the next createProcessor() reuses it to avoid WASM 19MB memory leak, dont rebuild a new one.
    canRebuildProcessor() {
      return false;
    },

    checkCompatibility() {
      const denoiser = getDenoiserExtension();
      if (!denoiser) {
        // Optimistic: `isDeNoiserCompatibleSync` (stream.service.ts) relies on
        // this returning `true` so the UI doesn't hide noise-cancellation during
        // the brief async SDK load. Real compatibility is re-checked once the
        // SDK resolves (see `isDeNoiserCompatible`).
        return true;
      }
      return denoiser.checkCompatibility();
    },

    getName() {
      return 'v1';
    }
  };
}

/** Reset hook-scoped state — use in tests only. */
export function _resetDenoiserV1StateForTests(): void {
  cachedProcessor = null;
}
